import cn from 'classnames';
import { IPlayImageObjectItem } from 'common/modules/ReactShared';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useImageErrorHandler } from 'students/views/shared/bundles/media/useImageErrorHandler';
import SvgAudioPlayer from 'students/views/shared/components/MediaPlayer/SvgAudioPlayer';
import { useAnimateScenarios, useBreakPoint } from 'students/views/shared/hooks';
import { getElementTargetPosition } from '../../common/helpers';
import { usePlayAudioPlayer } from '../../common/hooks';
import successPoint from './assets/SuccessPoint.svg';
import { animationScenarios } from './assets/animation/scenarios';
import { PointFailAnimated, PointSuccessAnimated } from './components';
import {
  SBodyContainer,
  SHeadContainer,
  SImg,
  SImgContainer,
  SInstruction,
  SItemContainer,
  SSuccessPoint
} from './components/styled';
import { IPointOptions } from './types';

export interface ISelectAnswerPayload {
  itemId: number;
  mistakesCount: number;
}

const iconSizeRem = {
  height: 6,
  width: 6
};

const iconMobileSizeRem = {
  height: 4,
  width: 4
};

const successIconSizeMobilePx = {
  height: 26,
  width: 26
};

const successIconSizeDesktopPx = {
  height: 44,
  width: 44
};

const CORRECT_POINT_ANIMATION_TIMEOUT = 730;
const INCORRECT_POINT_ANIMATION_TIMEOUT = 1500;

interface IProps {
  item: IPlayImageObjectItem;
  points: IPointOptions[];
  imageURL: string | null;
  imageSize: { height: number; width: number } | null;
  currentIndex?: number;
  className?: string;
  onSelectAnswer: (payload: ISelectAnswerPayload) => void;
}

const ImageObjectItem: React.FC<IProps> = ({
  currentIndex = 0,
  item,
  points,
  onSelectAnswer,
  imageURL,
  imageSize,
  className
}) => {
  const pickEnabledRef = useRef(true);
  const [wrongAnswer, setWrongAnswer] = useState<IPointOptions | null>(null);
  const [successAnswer, setSuccessAnswer] = useState<IPointOptions | null>(null);
  const mistakesCountRef = useRef<number>(0);
  const { playCorrectSound, playIncorrectSound } = usePlayAudioPlayer();
  const isMobile = useBreakPoint('sm', true);
  const handleImageError = useImageErrorHandler('image-object-task');
  const imgContainerRef = useRef<HTMLDivElement | null>(null);
  const correctPointTimeoutRef = useRef<null | number>(null);
  const incorrectPointTimeoutRef = useRef<null | number>(null);

  const [instructionControls, animateInstructionControls] =
    useAnimateScenarios(animationScenarios);
  const [audioControls, animateAudioControls] = useAnimateScenarios(animationScenarios);
  const [bodyControls, animateBodyControls] = useAnimateScenarios(animationScenarios);
  const [imageControls, animateImageControls] = useAnimateScenarios(animationScenarios);

  const imageHeight = imageSize?.height;
  const imageWidth = imageSize?.width;
  const imageContainerHeight = imgContainerRef.current?.offsetHeight;

  const [maxImageHeight, maxImageWidth] = useMemo(() => {
    if (!imageWidth || !imageHeight || !imageContainerHeight) return [0, 0];
    const width = (imageWidth / imageHeight) * imageContainerHeight;
    const height = imageContainerHeight;
    return [height, width];
  }, [imageHeight, imageWidth, imageContainerHeight]);

  // reset attempts count
  useEffect(() => {
    mistakesCountRef.current = 0;
  }, [item.id]);

  // Animate header
  useEffect(() => {
    const scenario = currentIndex === 0 ? 'header-initial' : 'header-regular';
    if (item.audioURL) {
      animateAudioControls([scenario]);
    } else {
      animateInstructionControls([scenario]);
    }
  }, [
    animateAudioControls,
    animateInstructionControls,
    currentIndex,
    item.audioURL,
    item.instruction
  ]);

  // Center image
  const scrollImageToCenter = useCallback(() => {
    const container = imgContainerRef.current;
    if (!container) return;
    container.scrollTo({
      left: maxImageWidth / 2 - window.innerWidth / 2,
      behavior: 'smooth'
    });
  }, [maxImageWidth]);

  // Animate body
  useEffect(() => {
    animateBodyControls(['body-appear', isMobile ? 'body-zoomed' : 'body-default']);
    if (isMobile) {
      animateImageControls(
        [
          2,
          {
            height: maxImageHeight,
            width: maxImageWidth,
            transition: { type: 'Inertia', duration: 1 }
          }
        ],
        scrollImageToCenter
      );
    }
  }, [
    animateBodyControls,
    animateImageControls,
    scrollImageToCenter,
    maxImageHeight,
    maxImageWidth,
    isMobile
  ]);

  const autoPlayDelay = currentIndex === 0 ? 2200 : 1200;
  const currentIconSize = isMobile ? iconMobileSizeRem : iconSizeRem;
  const successIconSize = isMobile ? successIconSizeMobilePx : successIconSizeDesktopPx;

  const hideHeaderAnimation = () => {
    if (item.audioURL) animateAudioControls(['header-hidden']);
    else animateInstructionControls(['header-hidden']);
  };

  const processCorrectAnswer = () => {
    correctPointTimeoutRef.current && clearTimeout(correctPointTimeoutRef.current);

    playCorrectSound();

    pickEnabledRef.current = false;

    setWrongAnswer(null);
    setSuccessAnswer({
      top: item.top + item.height / 2,
      left: item.left + item.width / 2
    });
    hideHeaderAnimation();

    correctPointTimeoutRef.current = window.setTimeout(() => {
      setSuccessAnswer(null);
      pickEnabledRef.current = true;

      onSelectAnswer({
        itemId: item.id,
        mistakesCount: mistakesCountRef.current
      });
    }, CORRECT_POINT_ANIMATION_TIMEOUT);
  };

  const processWrongAnswer = (payload: IPointOptions) => {
    incorrectPointTimeoutRef.current && clearTimeout(incorrectPointTimeoutRef.current);

    playIncorrectSound();

    mistakesCountRef.current += 1;
    setSuccessAnswer(null);
    setWrongAnswer(payload);

    incorrectPointTimeoutRef.current = window.setTimeout(() => {
      setWrongAnswer(null);
    }, INCORRECT_POINT_ANIMATION_TIMEOUT);
  };

  const handleClick = (event: React.MouseEvent): void => {
    if (!pickEnabledRef.current) {
      return;
    }

    const [targetX, targetY] = getElementTargetPosition(event);

    const [topX, topY, bottomX, bottomY] = [
      item.left,
      item.top,
      item.left + item.width,
      item.top + item.height
    ];

    if (targetX >= topX && targetX <= bottomX && targetY >= topY && targetY <= bottomY) {
      processCorrectAnswer();
    } else {
      processWrongAnswer({ top: targetY, left: targetX });
    }
  };

  return (
    <SItemContainer className={cn(className)}>
      <SHeadContainer dir="ltr">
        <AnimatePresence>
          {!item.audioURL ? (
            <SInstruction
              as={motion.div}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={instructionControls}
              exit={{
                opacity: 0,
                scale: 1.5,
                transition: { type: 'Inertia', duration: 0.5, delay: 0.5 }
              }}
            >
              {item.instruction}
            </SInstruction>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={audioControls}
              exit={{
                opacity: 0,
                scale: 1.5,
                transition: { type: 'Inertia', duration: 0.5, delay: 0.5 }
              }}
            >
              <SvgAudioPlayer
                src={item.audioURL}
                autoPlayDelay={autoPlayDelay}
                color="#639cd4"
                progressColor="#ffffff"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </SHeadContainer>

      <SBodyContainer
        layout="position"
        ref={imgContainerRef}
        as={motion.div}
        initial={{
          x: 0,
          y: -40,
          opacity: 0
        }}
        animate={bodyControls}
      >
        <SImgContainer as={motion.div} animate={imageControls}>
          {!!imageURL && (
            <SImg
              onClick={handleClick}
              role="image"
              src={imageURL}
              alt="Image object"
              onError={handleImageError}
            />
          )}

          {!!wrongAnswer && (
            <PointFailAnimated coordinates={wrongAnswer} size={currentIconSize} />
          )}

          {!!successAnswer && (
            <PointSuccessAnimated coordinates={successAnswer} size={currentIconSize} />
          )}

          {points.map(({ top, left }) => (
            <SSuccessPoint
              key={top * left}
              src={successPoint}
              top={top}
              left={left}
              height={successIconSize.height}
              width={successIconSize.width}
            />
          ))}
        </SImgContainer>
      </SBodyContainer>
    </SItemContainer>
  );
};

export default ImageObjectItem;
